import React from 'react';
import {
  PencilIcon as HeroPencilIcon,
  TrashIcon as HeroTrashIcon,
  PlusIcon as HeroPlusIcon,
  MagnifyingGlassIcon as HeroMagnifyingGlassIcon,
  XMarkIcon as HeroXMarkIcon,
  ArrowLeftIcon as HeroArrowLeftIcon,
  FunnelIcon as HeroFunnelIcon,
  ChevronDownIcon as HeroChevronDownIcon,
  Squares2X2Icon as HeroSquares2X2Icon,
  ListBulletIcon as HeroListBulletIcon,
  CalendarIcon as HeroCalendarIcon,
  EllipsisHorizontalIcon as HeroEllipsisHorizontalIcon,
  BarsArrowUpIcon as HeroBarsArrowUpIcon,
  BarsArrowDownIcon as HeroBarsArrowDownIcon,
  AdjustmentsHorizontalIcon as HeroAdjustmentsHorizontalIcon,
  ChevronRightIcon as HeroChevronRightIcon,
  Bars3Icon as HeroBars3Icon,
  CheckIcon as HeroCheckIcon,
  GlobeAltIcon as HeroGlobeAltIcon,
  LinkIcon as HeroLinkIcon,
  ArrowPathIcon as HeroArrowPathIcon,
  ArrowUturnLeftIcon as HeroArrowUturnLeftIcon,
  ExclamationCircleIcon as HeroExclamationCircleIcon,
  BuildingOffice2Icon as HeroBuildingOffice2Icon,
  ComputerDesktopIcon as HeroComputerDesktopIcon,
  DocumentMagnifyingGlassIcon as HeroDocumentMagnifyingGlassIcon,
  DocumentArrowDownIcon as HeroDocumentArrowDownIcon,
  MinusCircleIcon as HeroMinusCircleIcon,
  CheckCircleIcon as HeroCheckCircleIcon,
  ClockIcon as HeroClockIcon,
  EllipsisVerticalIcon as HeroEllipsisVerticalIcon,
  NoSymbolIcon as HeroNoSymbolIcon,
} from '@heroicons/react/24/outline';

/**
 * Iconos para el componente MasterCrud
 * Basados en Heroicons 24/outline
 *
 * @see docs/icons.md - Sistema de iconos
 */

/** Icono de círculo con menos (Desactivar) */
export const MinusCircleIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroMinusCircleIcon className={className} aria-hidden="true" />
);

/** Icono de círculo con check (Activar) */
export const CheckCircleIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroCheckCircleIcon className={className} aria-hidden="true" />
);

/** Icono de documento con lupa (Auditoría) */
export const DocumentMagnifyingGlassIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroDocumentMagnifyingGlassIcon className={className} aria-hidden="true" />
);

/** Icono de documento con flecha abajo (Descargar) */
export const DocumentArrowDownIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroDocumentArrowDownIcon className={className} aria-hidden="true" />
);

/** Icono de monitor / escritorio */
export const ComputerDesktopIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroComputerDesktopIcon className={className} aria-hidden="true" />
);

/** Icono de advertencia en círculo */
export const ExclamationCircleIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroExclamationCircleIcon className={className} aria-hidden="true" />
);

/** Icono de edificio / oficina */
export const BuildingOffice2Icon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroBuildingOffice2Icon className={className} aria-hidden="true" />
);

/**
 * Icono compuesto "Habilitar en compañía": edificio azul + check verde (prototipo).
 * Es el mismo icono que usa el botón de acción "Habilitar por compañía" del listado;
 * se comparte con el modal de habilitación para unificar el lenguaje visual.
 *
 * @param className Controla el tamaño del contenedor (por defecto 18×18).
 */
export const EnableCompanyIcon: React.FC<{ className?: string }> = ({ className = 'w-[18px] h-[18px]' }) => (
  <span className={`relative inline-flex ${className}`}>
    <svg className="w-full h-full text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3.75h9a.75.75 0 0 1 .75.75V21M4.5 3.75V21M13.5 21V9.75l6 3v8.25M8.25 6.75h.008v.008H8.25V6.75Zm3 0h.008v.008h-.008V6.75Zm-3 3h.008v.008H8.25V9.75Zm3 0h.008v.008h-.008V9.75Zm-3 3h.008v.008H8.25v-.008Zm3 0h.008v.008h-.008v-.008Z" />
    </svg>
    <span className="absolute -bottom-1 -right-1 bg-white dark:bg-dark-bg-primary rounded-sm p-px">
      <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    </span>
  </span>
);

/** Icono de deshacer / revertir */
export const RevertIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroArrowUturnLeftIcon className={className} aria-hidden="true" />
);

/** Icono de revertir / rotar */
export const ArrowPathIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroArrowPathIcon className={className} aria-hidden="true" />
);

/** Icono de enlace (link) */
export const LinkIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroLinkIcon className={className} aria-hidden="true" />
);

/** Icono de globo terráqueo */
export const GlobeAltIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroGlobeAltIcon className={className} aria-hidden="true" />
);

/** Icono de verificación (check) */
export const CheckIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroCheckIcon className={className} aria-hidden="true" />
);

/** Icono de hamburguesa (3 rayas) */
export const Bars3Icon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroBars3Icon className={className} aria-hidden="true" />
);

/** Icono de ajustes/filtros horizontales */
export const AdjustmentsIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroAdjustmentsHorizontalIcon className={className} aria-hidden="true" />
);

/** Icono de flecha a la derecha */
export const ChevronRightIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroChevronRightIcon className={className} aria-hidden="true" />
);

/** Icono de lápiz para editar */
export const PencilIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroPencilIcon className={className} aria-hidden="true" />
);

/** Icono de papelera para eliminar */
export const TrashIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroTrashIcon className={className} aria-hidden="true" />
);

/** Icono de más para crear */
export const PlusIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroPlusIcon className={className} aria-hidden="true" />
);

/** Icono de lupa para buscar */
export const SearchIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroMagnifyingGlassIcon className={className} aria-hidden="true" />
);

/** Icono de X para cerrar/cancelar */
export const CloseIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroXMarkIcon className={className} aria-hidden="true" />
);

/** Icono de flecha izquierda para volver */
export const BackIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroArrowLeftIcon className={className} aria-hidden="true" />
);

/** Icono de embudo para filtrar */
export const FilterIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroFunnelIcon className={className} aria-hidden="true" />
);

/** Alias de FilterIcon */
export const FunnelIcon = FilterIcon;

/** Icono de chevron abajo para dropdowns */
export const ChevronDownIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroChevronDownIcon className={className} aria-hidden="true" />
);

/** Icono de grid (cuadrícula 2x2) */
export const GridIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroSquares2X2Icon className={className} aria-hidden="true" />
);

/** Icono de lista */
export const ListIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroListBulletIcon className={className} aria-hidden="true" />
);

/** Icono de calendario */
export const CalendarIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroCalendarIcon className={className} aria-hidden="true" />
);

/** Icono de elipsis horizontal (puntos suspensivos) */
export const EllipsisHorizontalIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroEllipsisHorizontalIcon className={className} aria-hidden="true" />
);

/** Icono de drag handle (6 puntos) */
export const DragHandleIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg 
    viewBox="0 0 20 20" 
    fill="currentColor" 
    className={className}
    aria-hidden="true"
  >
    <path d="M7 4a1 1 0 100-2 1 1 0 000 2zM13 4a1 1 0 100-2 1 1 0 000 2zM7 11a1 1 0 100-2 1 1 0 000 2zM13 11a1 1 0 100-2 1 1 0 000 2zM7 18a1 1 0 100-2 1 1 0 000 2zM13 18a1 1 0 100-2 1 1 0 000 2z" />
  </svg>
);

/** Icono de orden ascendente */
export const SortAscIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroBarsArrowUpIcon className={className} aria-hidden="true" />
);

/** Icono de orden descendente */
export const SortDescIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroBarsArrowDownIcon className={className} aria-hidden="true" />
);

/** Icono de reloj para historial de auditoría */
export const ClockIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroClockIcon className={className} aria-hidden="true" />
);

/** Icono de puntos verticales (kebab vertical — "Más acciones/opciones") */
export const EllipsisVerticalIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroEllipsisVerticalIcon className={className} aria-hidden="true" />
);

/** Icono de símbolo prohibido (Desactivar) */
export const NoSymbolIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <HeroNoSymbolIcon className={className} aria-hidden="true" />
);
